import { Feature, MultiPolygon, Polygon } from "geojson";

import { MAX_PLANNER_EXTENDED_ADDRESS, PolygonStatus } from "fond/project/polygon";
import { PolygonState } from "fond/project/polygon/redux";
import type { BaseDataTaskRecord } from "fond/redux/baseDataTasks";
import { LayerId, LayerIds, Store } from "fond/types";
import type { BaseDataLayerName } from "fond/types/layer";

import { BOUNDARY_TOO_LARGE } from "../copy";
import { BASE_DATA_LAYER_KEYS, BASE_DATA_LAYERS, BaseDataLayerKey } from "../layers";
import type { BaseDataTaskSource } from "../taskPhases";

export type BoundaryImportKey = Extract<BaseDataLayerKey, "addresses" | "parcels" | "streets" | "aerial">;

/** Base data layers that FOND data can be imported into, in display order: those whose planner layers all have an import source. */
export const BOUNDARY_IMPORT_KEYS = BASE_DATA_LAYER_KEYS.filter((key) => {
  const { layers } = BASE_DATA_LAYERS[key];
  return layers.length > 0 && layers.every(({ importSource }) => importSource);
}) as BoundaryImportKey[];

/** Whether FOND base data can be imported into a base data layer. */
export const isBoundaryImportKey = (key: BaseDataLayerKey): key is BoundaryImportKey => (BOUNDARY_IMPORT_KEYS as BaseDataLayerKey[]).includes(key);

/** A planner layer FOND data is imported into, and the FOND layer downloaded for it. */
export type ImportLayer = { id: LayerId; source: BaseDataLayerName };

/** Return the planner layers an import of a base data layer's FOND data writes to, with the FOND layer downloaded for each. */
export const getImportLayers = (key: BoundaryImportKey): ImportLayer[] =>
  BASE_DATA_LAYERS[key].layers.flatMap(({ id, importSource }) => (importSource ? [{ id, source: importSource }] : []));

/** Return the polygon select layers that import the FOND data of base data layers. */
export const getImportSources = (keys: readonly BoundaryImportKey[]): BaseDataLayerName[] =>
  keys.flatMap((key) => getImportLayers(key).map(({ source }) => source));

/** Polygon select states of a polygon file being read, rather than of an area being drawn on the map. */
export const POLYGON_FILE_STATES: readonly string[] = [PolygonState.modeSelect, PolygonState.uploadingPolygon, PolygonState.complete];

/** Whether a task of the given kind is still reading its polygon file, before anything is saved or imported. */
export const isReadingPolygonFile = (record: BaseDataTaskRecord | undefined, source: BaseDataTaskSource): boolean =>
  record?.source === source && record.status === "running" && record.stage === "readingFile";

/** Base data layer offered alongside each layer that imports FOND data from an area, if any. */
const IMPORT_COMPANIONS: Partial<Record<BoundaryImportKey, BoundaryImportKey>> = { addresses: "parcels", parcels: "addresses" };

/** Return the base data layer offered alongside a layer's FOND data import, or null when it imports alone. */
export const getImportCompanion = (key: BoundaryImportKey): BoundaryImportKey | null => IMPORT_COMPANIONS[key] ?? null;

/** Return base data layers in display order. */
export const sortBoundaryImportKeys = (keys: readonly BoundaryImportKey[]): BoundaryImportKey[] =>
  BOUNDARY_IMPORT_KEYS.filter((key) => keys.includes(key));

type PolygonSelectState = Store["project"]["polygon"];

/** Whether polygon select is open to select an area to import a base data layer's FOND data from. */
export const isImportAreaFor = (
  { isOpen }: Pick<PolygonSelectState, "isOpen">,
  activeLayerGroupId: string | null | undefined,
  key: BoundaryImportKey
): boolean => isOpen && activeLayerGroupId === BASE_DATA_LAYERS[key].layerGroupId;

/** Return why the boundary drawn in polygon select can't be used to import FOND data, if it can't. */
export const getBoundaryWarning = ({ isTooBig, status }: Pick<PolygonSelectState, "isTooBig" | "status">): string | null => {
  if (isTooBig) return BOUNDARY_TOO_LARGE;
  if (status === PolygonStatus.InvalidArea) return "The boundary overlaps itself. Redraw it to import FOND data.";
  return null;
};

/** Return why the FOND data of a base data layer can't be imported from the area selected in polygon select, if it can't. */
export const getImportAreaWarning = (
  { isTooBig, status, selectedData }: Pick<PolygonSelectState, "isTooBig" | "status" | "selectedData">,
  key: BoundaryImportKey
): string | null => {
  if (isTooBig) return "The area is too large to import FOND data from.";
  if (status === PolygonStatus.InvalidArea) return "The area overlaps itself. Redraw it to import FOND data.";
  if (status === PolygonStatus.QueryTooLarge) return "There are too many features inside the area to import.";
  if (status !== PolygonStatus.ValidArea) return null;
  const counts = getSelectedCounts(selectedData, key);
  if (counts.every((count) => count === 0)) return `FOND doesn't have ${BASE_DATA_LAYERS[key].noun} for this area yet.`;
  return key === "addresses" ? getAddressLimitWarning(selectedData) : null;
};

/** Return why the addresses selected in polygon select can't be imported into planner extended, if they can't. */
export const getAddressLimitWarning = (selectedData: PolygonSelectState["selectedData"]): string | null =>
  getSelectedCounts(selectedData, "addresses")[0] > MAX_PLANNER_EXTENDED_ADDRESS
    ? `The area contains more than ${MAX_PLANNER_EXTENDED_ADDRESS.toLocaleString()} addresses. Draw a smaller area to import addresses.`
    : null;

/** Return the number of features polygon select would import into each planner layer of a base data layer. */
export const getSelectedCounts = (selectedData: PolygonSelectState["selectedData"], key: BoundaryImportKey): number[] =>
  getImportLayers(key).map(({ source }) => selectedData?.[source]?.features.length ?? 0);

/** Layers passed to polygon select so that addresses, parcels and streets are imported by default. */
export const BOUNDARY_POLYGON_SELECT_LAYER_IDS: LayerId[] = [LayerIds.inParcel, LayerIds.inStreet];

/** Whether polygon select is open for the boundary rather than for a layer's area select. */
export const selectIsBoundarySelectOpen = (state: Store): boolean => state.project.polygon.isOpen && !state.project.activeLayerGroupId;

/** Return the polygon as a MultiPolygon feature, as stored in `version.UploadedArea`. */
export const toMultiPolygonFeature = (feature: Feature<Polygon | MultiPolygon>): Feature<MultiPolygon> =>
  feature.geometry.type === "MultiPolygon"
    ? (feature as Feature<MultiPolygon>)
    : { ...feature, geometry: { type: "MultiPolygon", coordinates: [feature.geometry.coordinates] } };
